import { Grid, InputAdornment } from "@mui/material";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { useStyles } from "components/Layout/Member/MembersConfiguration/MembersConfiguration.styles";
import { Field, useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import { OLPaper } from "OLPaper";
import { Fragment, useState } from "react";
import { CustomEventDropdown } from "./CustomEventDropdown";

export function ActiveMemberSection() {
  const { message } = useMessage();
  const classes = useStyles();
  const conditions = ["transaction", "customEvent"];
  const { values, setFieldValue } = useFormikContext<any>();
  const [selectedConditions, setSelectedConditions] = useState([
    ...(values.activeMember?.transactionInXDays ? ["transaction"] : []),
    ...(values.activeMember?.customEventsInXDays ? ["customEvent"] : []),
  ]);
  const availableConditions = conditions.filter(
    (condition) => !selectedConditions.includes(condition),
  );
  const areAvailableConditions = availableConditions?.length > 0;
  const removeSelectedCondition = (item: string) => {
    setSelectedConditions([
      ...selectedConditions.filter((condition) => condition !== item),
    ]);
    if (item === "transaction") {
      setFieldValue("transactionCondition", undefined);
    } else if (item === "customEvent") {
      setFieldValue("customEventCondition", undefined);
    }
  };

  return (
    <OLPaper className={classes.OLPaper}>
      <div className={classes.form}>
        <Field name="activeMember">
          {({ form }: { form: any }) => (
            <>
              {selectedConditions?.map((item: string, index: number) => (
                <Fragment key={index}>
                  {index > 0 && (
                    <OLMenuButton
                      buttonVariant="text"
                      className={classes.operatorButton}
                      data-test-id="active-member-settings-operator-select"
                      endIcon={<ChevronDownIcon />}
                      items={[
                        {
                          onClick: () =>
                            form.setFieldValue("activeMember.operator", "and"),
                          label: message({ id: "and" }).toUpperCase(),
                        },
                        {
                          onClick: () =>
                            form.setFieldValue("activeMember.operator", "or"),
                          label: message({ id: "or" }).toUpperCase(),
                        },
                      ]}
                      hideSearch
                      submit={message({
                        id: values.activeMember.operator,
                      })}
                    />
                  )}
                  <div className={classes.conditionRow}>
                    {item === "transaction" ? (
                      <Grid container spacing={8}>
                        <Grid item xs={12}>
                          <SelectInput
                            className={classes.fullWidth}
                            data-test-id="active-member-settings-transaction-condition"
                            disabled={!areAvailableConditions}
                            items={conditions?.map((item) => ({
                              id: item,
                              label: message({ id: item }),
                            }))}
                            label={
                              <RequiredFieldLabel
                                text={message({ id: "condition" })}
                              />
                            }
                            name="transactionCondition"
                            onChange={() => {
                              form.setFieldValue(
                                "transactionCondition",
                                undefined,
                              );
                              form.setFieldValue(
                                "customEventCondition",
                                "customEvent",
                              );
                              setSelectedConditions(["customEvent"]);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextInput
                            className={classes.fullWidth}
                            data-test-id="active-member-settings-transaction-in-x-days"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {message({ id: "days" })}
                                </InputAdornment>
                              ),
                            }}
                            label={
                              <RequiredFieldLabel
                                text={message({
                                  id: "activeMemberTransactionCount",
                                })}
                              />
                            }
                            name="activeMember.transactionInXDays"
                            type="number"
                          />
                        </Grid>
                      </Grid>
                    ) : item === "customEvent" ? (
                      <Grid container spacing={8}>
                        <Grid item xs={12}>
                          <SelectInput
                            className={classes.fullWidth}
                            data-test-id="active-member-settings-custom-event-condition"
                            disabled={!areAvailableConditions}
                            items={conditions?.map((item) => ({
                              id: item,
                              label: message({ id: item }),
                            }))}
                            label={
                              <RequiredFieldLabel
                                text={message({ id: "condition" })}
                              />
                            }
                            name="customEventCondition"
                            onChange={() => {
                              form.setFieldValue(
                                "transactionCondition",
                                "transaction",
                              );
                              form.setFieldValue(
                                "customEventCondition",
                                undefined,
                              );
                              setSelectedConditions(["transaction"]);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextInput
                            className={classes.fullWidth}
                            data-test-id="active-member-settings-custom-events-in-x-days"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {message({ id: "days" })}
                                </InputAdornment>
                              ),
                            }}
                            label={
                              <RequiredFieldLabel
                                text={message({
                                  id: "activeMemberCustomEventCount",
                                })}
                              />
                            }
                            name="activeMember.customEventsInXDays.days"
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <CustomEventDropdown fieldName="activeMember.customEventsInXDays.eventTypes" />
                        </Grid>
                        <Grid item xs={6}>
                          <Toggle
                            className={classes.eventToggle}
                            data-test-id="active-member-settings"
                            label={message({ id: "selectAllEvents" })}
                            name="activeMember.customEventsInXDays.allEvents"
                          />
                        </Grid>
                      </Grid>
                    ) : (
                      <></>
                    )}
                    {!areAvailableConditions && (
                      <OLClearIcon
                        className={classes.clearIcon}
                        data-test-id={`active-member-settings-delete-condition-${
                          index + 1
                        }`}
                        onClick={() => removeSelectedCondition(item)}
                      />
                    )}
                  </div>
                </Fragment>
              ))}
              <OLMenuButton
                buttonVariant="contained"
                className={classes.button}
                data-test-id="active-member-settings-add-condition"
                disabled={!areAvailableConditions}
                items={availableConditions?.map((item) => ({
                  label: message({ id: item }),
                  onClick: () => {
                    selectedConditions.length > 0 &&
                      form.setFieldValue("activeMember.operator", "and");
                    setSelectedConditions([...selectedConditions, item]);
                    if (item === "transaction") {
                      form.setFieldValue("transactionCondition", "transaction");
                      form.setFieldValue("activeMember.transactionInXDays", 0);
                    } else if (item === "customEvent") {
                      form.setFieldValue("customEventCondition", "customEvent");
                      form.setFieldValue("activeMember.customEventsInXDays", {
                        eventTypes: [],
                        days: 0,
                        allEvents: false,
                      });
                    }
                  },
                }))}
                hideSearch
                submit={message({ id: "addCondition" })}
              />
            </>
          )}
        </Field>
      </div>
    </OLPaper>
  );
}
